import { Mail, RefreshCw } from 'lucide-react';
import React, { useState } from 'react';
import { BRAND_LOGO_SQUARE } from '../brandAssets';
import { db } from '../services/db';

interface EmailConfirmationScreenProps {
    email: string;
    onLogout: () => void;
}

export const EmailConfirmationScreen: React.FC<EmailConfirmationScreenProps> = ({ email, onLogout }) => {
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);

    React.useEffect(() => {
        if (timeLeft > 0) {
            const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timerId);
        }
    }, [timeLeft]);

    const handleResend = async () => {
        if (timeLeft > 0) return;
        
        setSending(true);
        setMessage('');
        setError('');
        try {
            await db.resendConfirmationEmail(email);
            setMessage('Confirmation email sent! Please check your inbox.');
            setTimeLeft(30); // Start 30s cooldown
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Failed to send email. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-cream relative flex flex-col items-center justify-center p-6 overflow-hidden">
            {/* Texture Background */}
            <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/crissxcross.png")' }}></div>

            <div className="w-full max-w-sm relative z-10">
                {/* Logo */}
                <div className="flex flex-col items-center mb-10">
                    <div className="relative mb-6">
                        <img 
                            src={BRAND_LOGO_SQUARE} 
                            alt="Good Shout logo" 
                            className="relative w-32 h-32 rounded-full shadow-2xl" 
                        />
                    </div>
                    <h1 className="font-display text-3xl text-brand-dark text-center">Check Your Inbox!</h1>
                </div>

                {/* Card */}
                <div className="bg-white rounded-3xl shadow-xl p-8 relative overflow-hidden border border-brand-cream-dark text-center">
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 bg-brand-cream rounded-full flex items-center justify-center text-brand-red">
                            <Mail size={32} />
                        </div>
                    </div>

                    <p className="text-brand-dark font-medium mb-6">
                        We've sent a confirmation link to <span className="font-bold">{email}</span>.
                        <br/><br/>
                        Please confirm your email to start shouting about your favorite places!
                    </p>

                    {message && (
                        <div className="bg-green-50 text-green-700 p-3 rounded-xl text-sm font-bold mb-4 animate-in fade-in">
                            {message}
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 text-brand-red p-3 rounded-xl text-sm font-bold mb-4 animate-in fade-in">
                            {error}
                        </div>
                    )}

                    <button 
                        onClick={handleResend}
                        disabled={sending || timeLeft > 0}
                        className="w-full py-3 bg-brand-dark text-white rounded-xl font-bold shadow-lg hover:bg-black transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2 mb-4"
                    >
                        {sending ? <RefreshCw className="animate-spin" size={18} /> : timeLeft > 0 ? `Resend in ${timeLeft}s` : 'Resend Confirmation Email'}
                    </button>

                    <button 
                        onClick={onLogout}
                        className="text-sm font-bold text-brand-dark-light hover:text-brand-red transition"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};
